import {
  type Control,
  type FieldValues,
  type Path,
  useWatch,
} from 'react-hook-form';

import { type GetMultiselectMapper } from './types';

/**
 * This hook is used to get the filters for the multiselect dropdowns
 * It takes in the control, the relevant filter keys that should be used to filter the dropdowns
 * and the map of the api filter values to the form values
 */
export const useMultiselectApiFilters = <
  ApiFilterValues extends object,
  FormFilterValues extends FieldValues,
>({
  control,
  relevantFilterKeys,
  map,
}: {
  control: Control<FormFilterValues>;
  relevantFilterKeys: (keyof ApiFilterValues)[];
  map?: GetMultiselectMapper<ApiFilterValues, FormFilterValues>;
}) => {
  const relevantMap = Object.entries(map ?? {}).reduce((acc, [key, value]) => {
    if (relevantFilterKeys?.includes(key as keyof ApiFilterValues)) {
      return { ...acc, [key]: value };
    }
    return acc;
  }, {});

  const values = useWatch({
    control,
    name: Object.values(relevantMap) as Path<FormFilterValues>[],
  });

  return Object.keys(relevantMap).reduce((acc, key, index) => {
    return { ...acc, [key]: values[index] };
  }, {}) as ApiFilterValues;
};
